import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/api';
import { toast } from 'sonner';
import NotificationsPanel from '@/components/shop-drawings/NotificationsPanel';
import {
  Plus, Search, Filter, Loader2, FileText, Clock, AlertTriangle,
  CheckCircle2, XCircle, ChevronRight, Building2, Calendar,
  Package, RefreshCw, AlertCircle, ArrowRight, Truck, Download
} from 'lucide-react';

const TRADES = [
  'Aluminium', 'Joinery', 'Steel', 'Glazing', 'HVAC', 'Fire',
  'Electrical', 'Plumbing', 'Ceilings', 'Flooring', 'Facade',
  'Roofing', 'Waterproofing', 'Other'
];

const STATUSES = [
  'Draft', 'In Preparation', 'Submitted to Contractor', 'Returned by Contractor',
  'Submitted to Consultant', 'Under Consultant Review', 'Approved',
  'Approved with Comments', 'Rejected', 'Superseded', 'Closed', 'On Hold'
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const STATUS_COLORS = {
  'Draft': 'bg-slate-100 text-slate-700',
  'In Preparation': 'bg-blue-100 text-blue-700',
  'Submitted to Contractor': 'bg-purple-100 text-purple-700',
  'Returned by Contractor': 'bg-amber-100 text-amber-700',
  'Submitted to Consultant': 'bg-indigo-100 text-indigo-700',
  'Under Consultant Review': 'bg-cyan-100 text-cyan-700',
  'Approved': 'bg-green-100 text-green-700',
  'Approved with Comments': 'bg-emerald-100 text-emerald-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Superseded': 'bg-gray-100 text-gray-700',
  'Closed': 'bg-slate-100 text-slate-700',
  'On Hold': 'bg-orange-100 text-orange-700'
};

const RISK_COLORS = {
  'Green': 'bg-green-100 text-green-700 border-green-200',
  'Amber': 'bg-amber-100 text-amber-700 border-amber-200',
  'Red': 'bg-red-100 text-red-700 border-red-200'
};

const PRIORITY_COLORS = {
  'Low': 'bg-slate-100 text-slate-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-amber-100 text-amber-700',
  'Urgent': 'bg-red-100 text-red-700'
};

const ShopDrawingsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [subcontractors, setSubcontractors] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [projectName, setProjectName] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [tradeFilter, setTradeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subcontractorFilter, setSubcontractorFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisWeek, setDueThisWeek] = useState(false);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPackage, setNewPackage] = useState({
    trade: '',
    subcontractor_id: '',
    title: '',
    description: '',
    work_area: '',
    manufacturing_lead_time_days: 14,
    delivery_lead_time_days: 3,
    priority: 'Medium',
    required_on_site_date: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (tradeFilter) params.append('trade', tradeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (subcontractorFilter) params.append('subcontractor_id', subcontractorFilter);
      if (riskFilter) params.append('risk_level', riskFilter);
      if (overdueOnly) params.append('overdue_only', 'true');
      if (dueThisWeek) params.append('due_this_week', 'true');

      const [packagesRes, statsRes, subsRes, projectRes] = await Promise.all([
        api.get(`/projects/${projectId}/shop-drawings?${params.toString()}`),
        api.get(`/projects/${projectId}/shop-drawings/stats`),
        api.get(`/projects/${projectId}/subcontractors`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}`)
      ]);

      setPackages(packagesRes.data.items || []);
      setTotal(packagesRes.data.total || 0);
      setStats(statsRes.data);
      setSubcontractors(subsRes.data || []);
      setProjectInfo(projectRes.data);
      // Extract project name for header
      if (projectRes.data?.project?.name) {
        setProjectName(projectRes.data.project.name);
      } else if (projectRes.data?.name) {
        setProjectName(projectRes.data.name);
      }
    } catch (error) {
      toast.error('Failed to load shop drawings');
    } finally {
      setLoading(false);
    }
  }, [projectId, search, tradeFilter, statusFilter, subcontractorFilter, riskFilter, overdueOnly, dueThisWeek]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!newPackage.trade || !newPackage.subcontractor_id || !newPackage.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post(`/projects/${projectId}/shop-drawings`, newPackage);
      toast.success('Shop drawing package created');
      setCreateDialogOpen(false);
      setNewPackage({
        trade: '',
        subcontractor_id: '',
        title: '',
        description: '',
        work_area: '',
        manufacturing_lead_time_days: 14,
        delivery_lead_time_days: 3,
        priority: 'Medium',
        required_on_site_date: ''
      });
      navigate(`/shop-drawings/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create package');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearch('');
    setTradeFilter('');
    setStatusFilter('');
    setSubcontractorFilter('');
    setRiskFilter('');
    setOverdueOnly(false);
    setDueThisWeek(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          {/* Back Button - consistent with other modules */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/projects/${projectId}`)}
            className="text-slate-600 hover:text-slate-900 mb-3 -ml-2 text-sm"
            data-testid="back-to-project-btn"
          >
            ← Back to Project
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900">
                Shop Drawing Tracker
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {projectName || 'Project'} - Track shop drawing submissions and approvals
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <NotificationsPanel projectId={projectId} onRefresh={loadData} />
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (tradeFilter) params.append('trade', tradeFilter);
                    if (statusFilter) params.append('status', statusFilter);
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/shop-drawings/export?${params.toString()}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) throw new Error('Export failed');
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `shop_drawings_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();
                  } catch (error) {
                    console.error('Export error:', error);
                    toast.error('Failed to export shop drawings');
                  }
                }}
                className="rounded-sm hidden sm:flex"
                data-testid="export-excel-btn"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden md:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={loading}
                className="rounded-sm"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
                data-testid="create-shop-drawing-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Package</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_packages}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-slate-500">Total</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.pending_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-blue-600">Pending</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.approved_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-green-600">Approved</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-red-600">
                    {stats.overdue_with_contractor + stats.overdue_with_consultant}
                  </p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-red-600">Overdue</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50 hidden sm:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.due_this_week}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-amber-600">Due This Week</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50/50 hidden lg:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.high_risk_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-orange-600">High Risk</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50 hidden lg:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.total_resubmissions}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-purple-600">Resubmissions</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="flex-1 min-w-0 sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search packages..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 rounded-sm"
                  />
                </div>
              </div>

              <Select value={tradeFilter || "all"} onValueChange={(v) => setTradeFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px] rounded-sm">
                  <SelectValue placeholder="Trade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Trades</SelectItem>
                  {TRADES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] rounded-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={subcontractorFilter || "all"} onValueChange={(v) => setSubcontractorFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px] rounded-sm">
                  <SelectValue placeholder="Subcontractor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subcontractors</SelectItem>
                  {subcontractors.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={riskFilter || "all"} onValueChange={(v) => setRiskFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-[120px] rounded-sm">
                  <SelectValue placeholder="Risk" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Risk</SelectItem>
                  <SelectItem value="Green">Green</SelectItem>
                  <SelectItem value="Amber">Amber</SelectItem>
                  <SelectItem value="Red">Red</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant={overdueOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setOverdueOnly(!overdueOnly)}
                  className="rounded-sm flex-1 sm:flex-none"
                >
                  <AlertTriangle className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Overdue</span>
                </Button>

                <Button
                  variant={dueThisWeek ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDueThisWeek(!dueThisWeek)}
                  className="rounded-sm flex-1 sm:flex-none"
                >
                  <Clock className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Due This Week</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadData}
                  className="rounded-sm"
                  data-testid="refresh-packages-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="rounded-sm"
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table / Mobile Cards */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No shop drawing packages found</p>
                <p className="text-sm text-slate-400 mt-1">Create your first package to get started</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="sm:hidden divide-y divide-slate-200">
                  {packages.map((pkg) => {
                    const cycle = pkg.current_cycle;
                    const isOverdue = cycle?.is_contractor_overdue || cycle?.is_consultant_overdue || cycle?.is_resubmission_overdue;

                    return (
                      <div
                        key={pkg.id}
                        className={`p-4 cursor-pointer hover:bg-slate-50 ${isOverdue ? 'bg-red-50/30' : ''}`}
                        onClick={() => navigate(`/shop-drawings/${pkg.id}`)}
                        data-testid={`shop-drawing-card-${pkg.package_ref}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-mono text-sm font-bold text-slate-900">{pkg.package_ref}</p>
                            <p className="text-sm text-slate-600 mt-0.5">{pkg.title}</p>
                          </div>
                          <Badge className={`rounded-sm text-xs ${STATUS_COLORS[pkg.status] || ''}`}>
                            {pkg.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700">
                            {pkg.trade}
                          </Badge>
                          <Badge className={`rounded-sm text-xs ${RISK_COLORS[pkg.risk_level] || ''}`}>
                            {pkg.risk_level}
                          </Badge>
                          <Badge variant="outline" className="rounded-sm text-xs">
                            Cycle {pkg.current_cycle_number || 0}
                          </Badge>
                        </div>
                        {(pkg.subcontractor_name || pkg.required_on_site_date) && (
                          <div className="mt-2 text-xs text-slate-500">
                            {pkg.subcontractor_name && <span>{pkg.subcontractor_name}</span>}
                            {pkg.subcontractor_name && pkg.required_on_site_date && <span className="mx-1">•</span>}
                            {pkg.required_on_site_date && <span>Required: {formatDate(pkg.required_on_site_date)}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-bold">Ref</TableHead>
                        <TableHead className="font-bold">Trade</TableHead>
                        <TableHead className="font-bold">Title</TableHead>
                        <TableHead className="font-bold hidden lg:table-cell">Subcontractor</TableHead>
                        <TableHead className="font-bold">Status</TableHead>
                        <TableHead className="font-bold">Cycle</TableHead>
                        <TableHead className="font-bold">Risk</TableHead>
                        <TableHead className="font-bold hidden md:table-cell">Due Date</TableHead>
                        <TableHead className="font-bold hidden lg:table-cell">Required</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {packages.map((pkg) => {
                        const cycle = pkg.current_cycle;
                        const isOverdue = cycle?.is_contractor_overdue || cycle?.is_consultant_overdue || cycle?.is_resubmission_overdue;

                        return (
                          <TableRow
                            key={pkg.id}
                            className={`cursor-pointer hover:bg-slate-50 ${isOverdue ? 'bg-red-50/30' : ''}`}
                            onClick={() => navigate(`/shop-drawings/${pkg.id}`)}
                            data-testid={`shop-drawing-row-${pkg.package_ref}`}
                          >
                            <TableCell className="font-mono text-sm font-medium">
                              {pkg.package_ref}
                            </TableCell>
                            <TableCell>
                              <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700">
                                {pkg.trade}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {pkg.title}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 hidden lg:table-cell">
                              {pkg.subcontractor_name || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={`rounded-sm text-xs ${STATUS_COLORS[pkg.status] || ''}`}>
                                {pkg.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="rounded-sm">
                                {pkg.current_cycle_number || 0}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={`rounded-sm text-xs ${RISK_COLORS[pkg.risk_level] || ''}`}>
                                {pkg.risk_level}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm hidden md:table-cell">
                              {cycle?.due_dates?.consultant_response_due
                                ? formatDate(cycle.due_dates.consultant_response_due)
                                : cycle?.due_dates?.contractor_review_due
                                  ? formatDate(cycle.due_dates.contractor_review_due)
                                  : '-'}
                            </TableCell>
                            <TableCell className="text-sm hidden lg:table-cell">
                              {formatDate(pkg.required_on_site_date)}
                            </TableCell>
                            <TableCell>
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                New Shop Drawing Package
              </DialogTitle>
              <DialogDescription>
                Create a new shop drawing submission package
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Trade *</Label>
                  <Select
                    value={newPackage.trade}
                    onValueChange={(v) => setNewPackage({ ...newPackage, trade: v, custom_trade: '' })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm">
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRADES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                      <SelectItem value="Specialist">Specialist (Enter manually)</SelectItem>
                    </SelectContent>
                  </Select>
                  {(newPackage.trade === 'Other' || newPackage.trade === 'Specialist') && (
                    <Input
                      value={newPackage.custom_trade || ''}
                      onChange={(e) => setNewPackage({ ...newPackage, custom_trade: e.target.value })}
                      placeholder="Enter trade name"
                      className="mt-2 rounded-sm"
                    />
                  )}
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Subcontractor *</Label>
                  <Select
                    value={newPackage.subcontractor_id}
                    onValueChange={(v) => setNewPackage({ ...newPackage, subcontractor_id: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm">
                      <SelectValue placeholder="Select subcontractor" />
                    </SelectTrigger>
                    <SelectContent>
                      {subcontractors.length === 0 ? (
                        <div className="px-2 py-4 text-sm text-slate-500 text-center">
                          No subcontractors added yet.<br/>
                          Add them in the Subcontractors module first.
                        </div>
                      ) : (
                        subcontractors.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.company_name} ({s.trade})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Title *</Label>
                <Input
                  value={newPackage.title}
                  onChange={(e) => setNewPackage({ ...newPackage, title: e.target.value })}
                  placeholder="e.g., Level 1 Shopfront - Type A"
                  className="mt-1 rounded-sm"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Description</Label>
                <Textarea
                  value={newPackage.description}
                  onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                  placeholder="Scope and details..."
                  className="mt-1 rounded-sm"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Work Area</Label>
                  <Input
                    value={newPackage.work_area}
                    onChange={(e) => setNewPackage({ ...newPackage, work_area: e.target.value })}
                    placeholder="e.g., Level 1, Zone A"
                    className="mt-1 rounded-sm"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Priority</Label>
                  <Select
                    value={newPackage.priority}
                    onValueChange={(v) => setNewPackage({ ...newPackage, priority: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Lead Time (days) *</Label>
                  <Input
                    type="number"
                    value={newPackage.manufacturing_lead_time_days + newPackage.delivery_lead_time_days}
                    onChange={(e) => {
                      const totalDays = parseInt(e.target.value) || 0;
                      // Split between manufacturing (80%) and delivery (20%)
                      const mfgDays = Math.round(totalDays * 0.8);
                      const deliveryDays = totalDays - mfgDays;
                      setNewPackage({ 
                        ...newPackage, 
                        manufacturing_lead_time_days: mfgDays,
                        delivery_lead_time_days: deliveryDays
                      });
                    }}
                    className="mt-1 rounded-sm"
                    placeholder="e.g., 17"
                  />
                  <p className="text-xs text-slate-400 mt-1">Total lead time from approval to delivery</p>
                </div>

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Required On Site</Label>
                  <Input
                    type="date"
                    value={newPackage.required_on_site_date}
                    onChange={(e) => setNewPackage({ ...newPackage, required_on_site_date: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
                
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Priority</Label>
                  <Select
                    value={newPackage.priority}
                    onValueChange={(v) => setNewPackage({ ...newPackage, priority: v })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Package
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ShopDrawingsPage;
